import React, { useState, useEffect } from 'react';
import { Filter, X, SlidersHorizontal, Search } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import PropertyCard from '../components/PropertyCard';
import { supabase } from '../lib/supabase';

// Liste des zones basée sur tes données (tu peux l'ajuster)
const ZONES_BENIN = [
  'Abomey-Calavi', 'Akpakpa', 'Cotonou', 'Fidjrossè', 'Ouidah', 'Porto-Novo', 
  'Calavi Kpota', 'Haie Vive', 'Cocotiers', 'Saint-Jean', 'Cadjehoun'
];

const Listings: React.FC = () => {
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [properties, setProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortOption, setSortOption] = useState<'recent' | 'price_asc' | 'price_desc'>('recent');

  // État des filtres
  const [filters, setFilters] = useState({
    transactionType: 'All', // Correspond à 'type' dans SQL
    zone: [] as string[],
    minPrice: 0,
    maxPrice: 100000000,
    propertyType: 'All', // Correspond à 'categorie' dans SQL
    bedrooms: 'All'
  });

  // 1. Initialisation via navigation (si on vient de la recherche Home)
  useEffect(() => {
    if (location.state) {
      const { transactionType, zone } = location.state as any;
      setFilters(prev => ({
        ...prev,
        transactionType: transactionType || 'All',
        zone: zone ? [zone] : [],
      }));
    }
  }, [location.state]);

  // 2. Fetch des données depuis Supabase avec filtres
  useEffect(() => {
    const fetchFilteredProperties = async () => {
      setLoading(true);
      try {
        let query = supabase
          .from('biens')
          .select('*')
          .eq('valide', true); // On ne montre que les biens validés

        // Filtre par Type (Location, Vente-Achat, Meublé)
        if (filters.transactionType !== 'All') {
          query = query.eq('type', filters.transactionType);
        }

        // Filtre par Catégorie (Appartement, Maison, etc.)
        if (filters.propertyType !== 'All') {
          query = query.eq('categorie', filters.propertyType);
        }

        // Filtre par Zones (Multi-sélection)
        if (filters.zone.length > 0) {
          query = query.in('zone', filters.zone);
        }

        // Filtre par Prix
        if (filters.minPrice > 0) query = query.gte('prix', filters.minPrice);
        if (filters.maxPrice > 0) query = query.lte('prix', filters.maxPrice);

        // Filtre par Chambres
        if (filters.bedrooms !== 'All') {
          query = query.gte('chambres', filters.bedrooms);
        }

        // Tri
        if (sortOption === 'price_asc') query = query.order('prix', { ascending: true });
        else if (sortOption === 'price_desc') query = query.order('prix', { ascending: false });
        else query = query.order('created_at', { ascending: false });

        const { data, error } = await query;
        if (error) throw error;
        setProperties(data || []);
      } catch (err) {
        console.error("Erreur fetch listings:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchFilteredProperties();
  }, [filters, sortOption]);

  const toggleZone = (z: string) => {
    setFilters(prev => ({
      ...prev,
      zone: prev.zone.includes(z) 
        ? prev.zone.filter(item => item !== z) 
        : [...prev.zone, z]
    }));
  };

  const resetFilters = () => {
    setFilters({
      transactionType: 'All',
      zone: [],
      minPrice: 0,
      maxPrice: 100000000,
      propertyType: 'All',
      bedrooms: 'All'
    });
  };

  return (
    <div className="min-h-screen relative bg-gray-50">
      {/* Hero Header */}
      <div className="bg-navy text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-2">Nos Biens Immobiliers</h1>
          <p className="text-blue-100 italic">Trouvez la perle rare au Bénin avec LDF</p>
        </div>
      </div>

      <div className="container mx-auto px-4 mt-8 flex flex-col lg:row items-start gap-8 pb-12">
        <div className="flex flex-col lg:flex-row w-full gap-8">
          
          {/* Sidebar Filtres */}
          <aside className={`
            fixed inset-y-0 left-0 z-50 w-72 bg-white p-6 shadow-2xl transform transition-transform duration-300 lg:translate-x-0 lg:static lg:shadow-none lg:bg-white lg:rounded-2xl lg:border lg:w-1/4
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `}>
            <div className="flex justify-between items-center lg:hidden mb-6">
              <h2 className="text-xl font-bold">Filtres</h2>
              <button onClick={() => setIsSidebarOpen(false)}><X /></button>
            </div>

            <div className="space-y-6">
              {/* Transaction Type */}
              <div>
                <h3 className="font-bold text-navy mb-3">Transaction</h3>
                <div className="space-y-2">
                  {['All', 'Location', 'Vente-Achat', 'Meublé'].map(t => (
                    <button
                      key={t}
                      onClick={() => setFilters({...filters, transactionType: t})}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${filters.transactionType === t ? 'bg-orange text-white font-bold' : 'bg-gray-50 hover:bg-gray-100 text-gray-600'}`}
                    >
                      {t === 'All' ? 'Tout afficher' : t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Budget */}
              <div>
                <h3 className="font-bold text-navy mb-3">Budget Max (FCFA)</h3>
                <input 
                  type="number"
                  value={filters.maxPrice}
                  onChange={(e) => setFilters({...filters, maxPrice: Number(e.target.value)})}
                  className="w-full p-2 border rounded-lg mb-2"
                />
                <input 
                  type="range" min="0" max="100000000" step="500000"
                  value={filters.maxPrice}
                  onChange={(e) => setFilters({...filters, maxPrice: Number(e.target.value)})}
                  className="w-full accent-orange"
                />
              </div>

              {/* Zones */}
              <div>
                <h3 className="font-bold text-navy mb-3">Zones</h3>
                <div className="space-y-1 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                  {ZONES_BENIN.map(z => (
                    <label key={z} className="flex items-center gap-2 cursor-pointer py-1">
                      <input type="checkbox" checked={filters.zone.includes(z)} onChange={() => toggleZone(z)} className="rounded text-orange" />
                      <span className="text-sm text-gray-600">{z}</span>
                    </label>
                  ))}
                </div>
              </div>

              <button onClick={resetFilters} className="w-full py-2 text-sm text-gray-500 hover:text-orange underline transition">
                Réinitialiser les filtres
              </button>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 w-full">
            {/* Toolbar */}
            <div className="bg-white p-4 rounded-xl shadow-sm mb-6 flex justify-between items-center border border-gray-100">
              <button onClick={() => setIsSidebarOpen(true)} className="lg:hidden flex items-center gap-2 bg-navy text-white px-4 py-2 rounded-lg">
                <Filter size={18}/> Filtres
              </button>
              
              <p className="font-medium text-navy">
                {loading ? 'Recherche...' : `${properties.length} biens trouvés`}
              </p>

              <select 
                value={sortOption} 
                onChange={(e) => setSortOption(e.target.value as any)}
                className="bg-gray-50 border-none text-sm rounded-lg focus:ring-0"
              >
                <option value="recent">Plus récents</option>
                <option value="price_asc">Prix croissant</option>
                <option value="price_desc">Prix décroissant</option>
              </select>
            </div>

            {/* Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-pulse">
                {[1,2,3,4].map(i => <div key={i} className="h-80 bg-gray-200 rounded-2xl"></div>)}
              </div>
            ) : properties.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                {properties.map(p => (
                  <PropertyCard key={p.id} property={p} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-white rounded-2xl border">
                <Search size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-bold text-navy">Aucun résultat</h3>
                <p className="text-gray-500">Essayez de modifier vos critères de recherche.</p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Listings;
